import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {CrudBuilderConfig} from './models/crud-builder-config';
import {ErrorFormatter} from '../../helpers/error-formatter';
import {FieldConfig} from './models/field-config';
import {CountriesQuery, CountriesService} from '../../state/countries';
import {RelationsQuery, RelationsService} from '../../state/relations';
import {BreadBuilderService} from '../../services/bread-builder/bread-builder.service';
import {VehicleTypesQuery, VehicleTypesService} from '../../state/vehicle-types';
import {TooltipsQuery, TooltipsService} from '../../state/tooltips';
import {CancellationPeriodsQuery, CancellationPeriodsService} from '../../state/cancellation-periods';
import {FormatService} from '../../services/format/format.service';
import {PeopleQuery, PeopleService} from '../../state/people';
import {ContractsQuery, ContractsService} from '../../state/contracts';
import {AddressesQuery, AddressesService} from '../../state/addresses';
import {RetirementTypesQuery, RetirementTypesService} from '../../state/retirement-types';
import {FinancialCompaniesQuery, FinancialCompaniesService} from '../../state/financial-companies';
import {PopularAppStatesService} from '@core/services/popular-app-states/popular-app-states.service';
import {EducationsQuery, EducationsService} from '@core/state/educations';
import {HomeTypesQuery, HomeTypesService} from '@core/state/home-types';
import {MortgageTypesQuery, MortgageTypesService} from '@core/state/mortgage-types';
import {LifeInsuranceQuery, LifeInsuranceService} from '@core/state/life-insurance';
import {ConstructionTypesService} from '@core/state/relation-configs/construction_types';
import {MaritalStatusesService} from '@core/state/marital-statuses';
import {HomesQuery, HomesService} from '@core/state/homes';
// import { CurrencyPipe } from '@angular/common';
import { Platform, PopoverController } from '@ionic/angular';
import {HelpPopoverPage} from './help-popover/help-popover.page';

import flatpickr from 'flatpickr';
import {Dutch} from 'flatpickr/dist/l10n/nl';
import {FieldGroup} from '@core/pages/crud/dependent-fields/field-group';
import * as moment from 'moment';


@Component({
  selector: 'app-crudbuilder',
  templateUrl: './crud-builder.component.html',
  styleUrls: ['./crud-builder.component.scss'],
})
export class CrudBuilderComponent implements AfterViewInit, OnChanges {

  static readonly DESIGN_NORMAL = 'normal';
  static readonly DESIGN_WIZARD = 'wizard';
  public MAX_DATE = moment().add(20, 'years').year();

  /**
   * unknown, web, mobile
   */
  public isMobile = false;

  public showZakelijkFields = false;
  public showLoondienstFields = false;

  public countries$ = this.countriesQuery.selectAll();
  public relations$ = this.relationsQuery.selectAll();
  public people$ = this.peopleQuery.selectAll();
  public vehicleTypes$ = this.vehicleTypesQuery.selectAll();
  public tooltips$ = this.tooltipsQuery.selectAll();
  public retirementTypes$ = this.retirementTypesQuery.selectAll();
  public financialCompanies$ = this.financialCompaniesQuery.selectAll();
  public cancellationPeriods$ = this.cancellationPeriodsQuery.selectAll();
  public contracts$ = this.contractsQuery.selectAll();
  public addresses$ = this.addressesQuery.selectAll();
  public lifeInsurance$ = this.lifeInsuranceQuery.selectAll();
  public homes$ = this.homesQuery.selectAll();
  public educations$ = this.educationsQuery.selectAll();

  public fields: Array<FieldConfig | string | FieldGroup> = [];
  public config: CrudBuilderConfig = new CrudBuilderConfig();

  public showDropdownField = false;

  // @Input('config') passedConfig: CrudBuilderConfig = new CrudBuilderConfig();
  @Input() design = 'normal';
  @Input() err = new ErrorFormatter();
  @Input() model: any = {};

  // Select more than one datepicker element
  @ViewChildren('startDatePicker', {read: ElementRef}) startDatePickerEl: QueryList<any>;

  @Input('config') set passedConfig(value: CrudBuilderConfig) {
    this.config = this.bread.processCrudBuilderConfig(value);
    this.fields = this.processConfigFields(this.config.fields);
    if (!this.err) {
      this.err = new ErrorFormatter();
    }

    this.updateCachedRelationConfigs();
    this.updateCachedNonRelationConfigs();

    console.log(this.err);
    console.log(this.fields);
    console.log(this.model);
  }

  get passedConfig() {
    return this.config;
  }

  ngOnChanges(changes: SimpleChanges) {
    // When model changes, it could be that the whole dependent fields need to be updated as well.
    if (changes.hasOwnProperty('model')) {
      this.fields = this.processConfigFields(this.config.fields);
    }
  }

  /**
   * Processes Config Fields to process what fields to show.
   * - Used on processing dependent fields, so that fields show depending on value of other fields.
   */
  processConfigFields(fields) {
    const processedFields = [];

    // Process for dependent fields.
    for (const field of fields) {
      if (field instanceof FieldGroup) {
        // Show fields here if, target.value == value
        if (this.model[field.dependsOnTarget] === field.showWhenValueOfTargetIs) {
          for (const dependentField of field.fields) {
            processedFields.push(dependentField);
          }
        }
      } else if (field instanceof FieldConfig) {
        processedFields.push(field);
      }
    }

    return processedFields;
  }

  ngAfterViewInit(): void {
    // always put flatpickr inside "ngAfterViewInit"
    setTimeout(() => {
      // if you noticed setTimeout was called before executing this code below
      // because it does not work properly with initial loading
      if (this.startDatePickerEl) {
        // Check if element already initialized before accessing nativeElement
        const dateInputs = this.startDatePickerEl.toArray();

        dateInputs.forEach(input => {
          flatpickr(input.nativeElement, {
            locale: Dutch,
            dateFormat: 'd-m-Y',
            maxDate: new Date('31-12-2070')
          });
        });
      }
    });
  }

  public dropdownChange(data: string): void {
    this.fields = this.processConfigFields(this.config.fields);
  }

  // TODO: This should not be here, not related to CrudBuilder at all.
  public maritalChange(data: string): void {
    if (data === '500') {
      this.model['relation_configuration_id'] = 501;
    }
  }

  constructor(
    private countries: CountriesService,
    private countriesQuery: CountriesQuery,
    private relationsService: RelationsService,
    private relationsQuery: RelationsQuery,
    private vehicleTypesService: VehicleTypesService,
    private vehicleTypesQuery: VehicleTypesQuery,
    private tooltipsService: TooltipsService,
    private tooltipsQuery: TooltipsQuery,
    private retirementTypesService: RetirementTypesService,
    private retirementTypesQuery: RetirementTypesQuery,
    private financialCompaniesService: FinancialCompaniesService,
    private financialCompaniesQuery: FinancialCompaniesQuery,
    private cancellationPeriodsService: CancellationPeriodsService,
    private cancellationPeriodsQuery: CancellationPeriodsQuery,
    private peopleService: PeopleService,
    private peopleQuery: PeopleQuery,
    private contractsService: ContractsService,
    private contractsQuery: ContractsQuery,
    private addressesService: AddressesService,
    private addressesQuery: AddressesQuery,
    private educationsService: EducationsService,
    public homeTypesQuery: HomeTypesQuery,
    private homeTypesService: HomeTypesService,
    public mortgageTypesQuery: MortgageTypesQuery,
    private mortgageTypesService: MortgageTypesService,
    public lifeInsuranceQuery: LifeInsuranceQuery, // Added by joseph (Life Insurance)
    public lifeInsuranceService: LifeInsuranceService, // Added by joseph (Life Insurance)
    private constructionTypesService: ConstructionTypesService,
    private homesQuery: HomesQuery,
    private homesService: HomesService,
    public bread: BreadBuilderService,
    public format: FormatService,
    public popularAppStatesService: PopularAppStatesService,
    public educationsQuery: EducationsQuery,
    private maritalStatusesService: MaritalStatusesService,
    // private currencyPipe: CurrencyPipe,
    public popoverController: PopoverController,
    private platform: Platform,
  ) {
    if (this.platform.is('mobile')) {
      this.isMobile = true;
    }
  }


  /**
   * The difference between non-relation configs and RelationConfigs are that
   * RelationConfigs are cached to a time limit.
   * Therefore, RelationConfigs are ok to fetch using get().subscribe() all the time.
   * Because that get().subscribe() will not do anything as long as the time limit is not consumed.
   *
   * Non-RelationConfig states however are not cached, so it makes sense to update them only
   * when necessary to avoid unnecessary requests.
   */
  updateCachedNonRelationConfigs() {
    // Loop through all config fields
    for (const field of this.config.fields) {
      if (field instanceof FieldConfig) {
        switch (field.type) {
          case 'financial_companies':
            console.log('financial companies');
            this.financialCompaniesService.get().subscribe();
            break;
          case FieldConfig.TYPE_HOMES:
            this.homesService.get().subscribe();
            break;
        }
      }
    }
    // If a field type associated with this dropdown is available, update the data
  }

  updateCachedRelationConfigs() {
    /**
     * Please put only cached RelationConfigs here.
     * Normal states should use the dropdown FieldConfig instead.
     */
    if (this.peopleQuery.getCount() === 0) {
      this.peopleService.get({
        params: {
          limit: 50
        }
      }).subscribe();
    }

    this.countries.get().subscribe();
    this.relationsService.get().subscribe();
    this.vehicleTypesService.get().subscribe();
    this.tooltipsService.get().subscribe();
    this.retirementTypesService.get().subscribe();
    this.cancellationPeriodsService.get().subscribe();
    this.contractsService.get().subscribe();
    this.educationsService.get().subscribe();
    this.constructionTypesService.get().subscribe();
    this.maritalStatusesService.get().subscribe();
    this.homeTypesService.get().subscribe();
    // this.addressesService.get().subscribe();

    // There has to be a better way to do this, but this should be fine for now.
    for (const field of this.config.fields) {
      if (field instanceof FieldConfig) {
        switch (field.type) {
          case FieldConfig.TYPE_PERSON:
            this.peopleService.get().subscribe();
            break;
          case FieldConfig.TYPE_MORTGAGE:
            this.mortgageTypesService.get().subscribe();
            break;
          case FieldConfig.TYPE_HOME_TYPES:
            this.homeTypesService.get().subscribe();
            break;
          case FieldConfig.TYPE_LIFE_INSURANCE:
            this.lifeInsuranceService.get().subscribe(); // Added by joseph (Life Insurance)
            break;
          case FieldConfig.TYPE_FINANCIAL_COMPANIES:
            this.financialCompaniesService.get().subscribe();
            break;
        }
      }
    }
  }

  foundTooltip(field: any) {
    /******
     field.name - is the name of the field from crud-builder config
     this.config.modelAlias - is the crud model name from crud-builder config
     ***********************************************/
    if (!this.config.modelAlias) return;

    let tooltipName = `${this.config.modelAlias}.${field.name}`;

    const queryFilter = ({value}) => value === tooltipName;
    let tooltip = null;

    this.tooltipsQuery.selectEntity(queryFilter)
      .subscribe(found => {
        tooltip = found;
      });

    return tooltip;
  }

  async showHelpInfo(field: any) {
    let tooltip = this.foundTooltip(field);

    const popover = await this.popoverController.create({
      component: HelpPopoverPage,
      cssClass: 'help-popover',
      translucent: true,
      componentProps: {
        fieldLabel: field.label,
        textInfo: tooltip.name
      }
    });
    return await popover.present();
  }
}
